/**
 * Understanding-map right-panel opener.
 *
 * Opens the "Current Understanding" view — the `/discovery` Progress tab's
 * hero, nearly verbatim — in the global right panel, without leaving the
 * conversation. Its caller is the in-chat memory-update card
 * (`components/brain/understandingCard.js`), which taps straight through to
 * `openProgressPanel`.
 *
 * Parity with the Progress tab is the contract: the same KPI tiles (minus
 * the Continue Discovery action tile — starting discovery from mid-chat is
 * not this surface's job), the same interactive polar chart with the center
 * percentage, and the same chevroned topic rows. Rows and wedges drill
 * in-panel into the shared topic detail (`right-panel:drill` — fade swap,
 * auto-wired back button; the /practices → session drill precedent), built
 * by the same `topicDetailPanelHelper` the Progress tab uses. All hero
 * markup comes from `components/progressHero.js` and the data model from
 * `core/progressData.js`, both shared with DiscoverySection, so the two
 * surfaces cannot drift.
 *
 * @module components/progressPanelDrillHelper
 */

import { eventBus } from '../core/utils.js';
import { PolarProgressChart } from './PolarProgressChart.js';
import { loadTopicProgress } from '../core/progressData.js';
import { renderProgressAside, renderTopicRow, bindTopicRows, setTopicRowHot } from './progressHero.js';
import { buildTopicDetailContent } from './topicDetailPanelHelper.js';
import { enterTopicDiscovery } from '../core/topicDiscoveryEntry.js';

/** Right-panel `contentType` for this view (a CSS class hook only). */
export const UNDERSTANDING_MAP_CONTENT_TYPE = 'understanding-map';

/** Placeholder rows drawn while the progress fetch is in flight. */
const SKELETON_ROW_COUNT = 6;

/**
 * Skeleton rows held while the progress fetch is in flight, so the panel
 * opens instantly and fills in rather than appearing empty.
 * @returns {string} HTML string
 * @private
 */
function renderSkeleton() {
  const row = `
    <div class="understanding-map__row-skeleton" aria-hidden="true">
      <span class="understanding-map__dot"></span>
      <span class="understanding-map__content">
        <span class="understanding-map__line understanding-map__line--title"></span>
        <span class="understanding-map__line understanding-map__line--blurb"></span>
      </span>
    </div>
  `;
  return row.repeat(SKELETON_ROW_COUNT);
}

/**
 * Drill from a map row/wedge into the shared topic detail — fade swap with
 * an auto-wired back button (falls back to a fresh open at the depth cap).
 * The detail content and its hydration are the same helper the Progress
 * tab's fresh open uses; "Explore this topic" rides as the drilled header's
 * primary button.
 * @param {Object} topic - Topic as `loadTopicProgress` shapes it
 * @private
 */
function drillIntoTopic(topic) {
  const { content, hydrate } = buildTopicDetailContent(topic);

  // Guards against double-fire while enterTopicDiscovery() is in flight.
  let workingOnTopic = false;
  const payload = {
    title: '',
    titleAlign: 'left',
    primaryButton: {
      label: 'Explore this topic',
      onClick: async () => {
        if (workingOnTopic) return;
        workingOnTopic = true;
        await enterTopicDiscovery(topic.id);
      }
    },
    content,
    contentType: 'discovery-topic'
  };

  const panel = window.app?.modules?.rightPanelManager;
  if (panel?.canDrill?.()) {
    eventBus.emit('right-panel:drill', payload);
  } else {
    eventBus.emit('right-panel:open', payload);
  }
  hydrate();
}

/**
 * Open the "Current Understanding" map in the right panel: the Progress
 * tab's hero (KPI tiles + interactive polar chart) over one chevroned topic
 * row per topic, each drilling into the shared topic detail.
 *
 * Opens immediately with skeleton rows and hydrates from the shared progress
 * model afterwards. A fetch failure leaves the panel open with a quiet line —
 * it never blocks the panel or the conversation behind it.
 *
 * @returns {void}
 */
export function openProgressPanel() {
  const container = document.createElement('div');
  container.className = 'understanding-map';
  container.innerHTML = `
    <div class="understanding-map__skeleton" data-map-skeleton>
      <div class="understanding-map__chart-skeleton" aria-hidden="true"></div>
      ${renderSkeleton()}
    </div>
    <div data-map-body></div>
  `;

  const skeletonEl = container.querySelector('[data-map-skeleton]');
  const bodyEl = container.querySelector('[data-map-body]');
  if (!skeletonEl || !bodyEl) return;

  let chart = null;
  // Set by `onDispose` when the panel closes or its content is swapped, so a
  // late hydration can't write into a replaced panel.
  let cancelled = false;

  eventBus.emit('right-panel:open', {
    title: 'Current Understanding',
    // Centered: this view's title names a place, not a record — it reads as a
    // section header over the map rather than a left-flush item label (the
    // drilled topic detail below keeps the left-flush default).
    titleAlign: 'center',
    content: container,
    contentType: UNDERSTANDING_MAP_CONTENT_TYPE,
    onDispose: () => {
      cancelled = true;
      if (chart) chart.destroy();
      chart = null;
    }
  });

  // Fire-and-forget: the panel is already on screen.
  (async () => {
    let model = null;
    try {
      model = await loadTopicProgress();
    } catch (err) {
      console.warn('[progressPanelDrillHelper] progress fetch failed:', err);
    }
    if (cancelled) return;

    skeletonEl.remove();
    if (!model) {
      bodyEl.innerHTML = '<p class="understanding-map__note">Couldn\'t load your map right now</p>';
      return;
    }
    if (model.topics.length === 0) {
      bodyEl.innerHTML = '<p class="understanding-map__note">Nothing saved yet — your first conversation starts the map.</p>';
      return;
    }

    const rowsHtml = model.topics
      .map(topic => renderTopicRow(topic, model.blurbs))
      .join('');
    bodyEl.innerHTML = `
      <div class="discovery-progress-card discovery-progress-card--panel">
        <div class="discovery-progress-hero">
          ${renderProgressAside(model.topics, model.overall)}
          <div class="discovery-polar" data-polar-chart></div>
        </div>
        <div class="session-cards-container discovery-topic-list">${rowsHtml}</div>
      </div>
    `;

    const openTopic = (topicId) => {
      const topic = model.topics.find(t => t.id === topicId);
      if (topic) drillIntoTopic(topic);
    };

    const list = bodyEl.querySelector('.discovery-topic-list');
    bindTopicRows(list, {
      onOpen: openTopic,
      getChart: () => chart
    });

    // Fail-soft: a chart that can't mount must still leave the rows usable.
    try {
      chart = new PolarProgressChart(bodyEl.querySelector('[data-polar-chart]'), {
        topics: model.topics.map(t => ({ id: t.id, progress: t.progress, label: t.label })),
        centerPct: model.overall,
        onWedgeHover: (topicId) => setTopicRowHot(list, topicId),
        onWedgeClick: openTopic
      });
    } catch (err) {
      console.warn('[progressPanelDrillHelper] polar chart mount failed:', err);
      chart = null;
    }
  })();
}
